import { createAsyncThunk } from '@reduxjs/toolkit';
import getEntriesInPlantDataUseCase from 'modules/inbound/entries/application/InPlant/GetEntriesInPlantDataUseCase';
import { getEntriesParalyzationUseCase } from 'modules/inbound/entries/application/InPlant/GetEntriesParalyzationUseCase';
import getFiltersQueryString from 'modules/inbound/shared/domain/GetFiltersQuery';
import {
  FiltersItem,
  NameValues
} from 'modules/inbound/shared/domain/NameValues';
import {
  getFilterSearch,
  getPageInfo,
  getSelectedEntryInPlant,
  getSelectedFilters,
  getSelectedParalyzationStatus,
  getSelectedColumn,
  getFiltervehicleSearch
} from '.';
import { getGlobalDlzSummary } from 'modules/inbound/entries/application/InPlant/GetGlobalDlzSummary';
import { getLastUpdateUseCase } from 'modules/inbound/shared/application/LastUpdateUseCases/GetLastUpdateUseCase';
import { LastUpdateType } from 'modules/inbound/shared/domain/LastUpdate/types';
import DownloadEntriesInPlantUseCase from 'modules/inbound/entries/application/InPlant/DownloadEntriesInPlantUseCase';
import { SelectedInPlantFilters } from './types';
import {
  ColumnSelected,
  Sort
} from 'modules/shared/layout/components/TableHeader/types';

const getEntriesInPlantData = createAsyncThunk(
  'entriesST12InPlant/getEntriesInPlant',
  async (_, { getState }) => {
    const appState = getState();
    const { page, size } = getPageInfo(appState);
    const selectedColumn: ColumnSelected = getSelectedColumn(appState);
    const selectedParalyzationStatus = getSelectedParalyzationStatus(appState);
    const vehicleSearch = getFiltervehicleSearch(appState);
    const { warehouses, lastPositions }: SelectedInPlantFilters =
      getSelectedFilters(appState);
    const searchValue = getFilterSearch(appState);

    const queryFilters: FiltersItem[] = [
      { name: NameValues.LAST_POSITION, values: lastPositions }
    ];

    !vehicleSearch?.length &&
      queryFilters.push({ name: NameValues.WAREHOUSE, values: warehouses });
    const sortOrder = selectedColumn.isAscending ? Sort.ASC : Sort.DESC;
    const sort = `{${selectedColumn.key}:${sortOrder}}`;

    let querySearch: string[] = [];
    querySearch = getFiltersQueryString(queryFilters);
    searchValue &&
      querySearch.push(`${NameValues.VEHICLE_PLATE_NUMBER}:LK:${searchValue}`);
    selectedParalyzationStatus && querySearch.push(selectedParalyzationStatus);

    vehicleSearch?.length > 0 &&
      querySearch.push(
        `${NameValues.TRASPORT_ID}:IN:(${vehicleSearch.join(';')})`
      );
    const querySearchString = querySearch.join('||');
    const queryParams = {
      page: page,
      size,
      sort: selectedColumn.key.length ? sort : '',
      search: querySearchString && `{${querySearchString}}`
      // booked: 0
    };

    const response = await getEntriesInPlantDataUseCase.execute({
      queryParams
    });

    return response;
  }
);

const getEntryInPlantDetailData = createAsyncThunk(
  'entriesST12InPlant/getEntryInPlantDetailData',
  async (_, { getState }) => {
    const appState = getState();
    const { transportId } = getSelectedEntryInPlant(appState);

    const response = await getEntriesInPlantDataUseCase.getDetailInPlant({
      transportId: transportId
    });

    return response;
  }
);

const getWarehouseDlzDetail = createAsyncThunk(
  'entriesST12InPlant/getWarehouseDlzDetail',
  async (_, { getState }) => {
    const appState = getState();
    const { transportId } = getSelectedEntryInPlant(appState);

    const response =
      await getEntriesInPlantDataUseCase.getEntryWarehouseDlzDetail({
        transportId: transportId
      });

    return response;
  }
);

const getParalyzationSummary = createAsyncThunk(
  'entriesST12InPlant/getParalyzationSummary',
  () => getEntriesParalyzationUseCase.execute()
);

const getGlobalDlzSummaryUrl = createAsyncThunk(
  'entriesST12InPlant/getGlobalDlzSummaryUrl',
  () => getGlobalDlzSummary.execute()
);

const getEntriesInPlantLastUpdateData = createAsyncThunk(
  'entriesST12InPlant/getEntriesInPlantLastUpdateData',
  () =>
    getLastUpdateUseCase.execute({
      queryParams: { key: LastUpdateType.entriesInPlant }
    })
);

const downloadEntriesInPlant = createAsyncThunk(
  'entriesST12/downloadEntriesInPlant',
  async (_, { getState }) => {
    const appState = getState();
    const selectedParalyzationStatus = getSelectedParalyzationStatus(appState);
    const { warehouses, lastPositions }: SelectedInPlantFilters =
      getSelectedFilters(appState);
    const searchValue = getFilterSearch(appState);

    let queryFilters: FiltersItem[] = [
      {
        name: NameValues.WAREHOUSE,
        values: warehouses
      },
      {
        name: NameValues.LAST_POSITION,
        values: lastPositions
      }
    ];

    let querySearch: string[] = [];
    querySearch = getFiltersQueryString(queryFilters);
    searchValue &&
      querySearch.push(
        `${NameValues.CODE}:LK:${searchValue}|OR|${NameValues.VEHICLE_PLATE_NUMBER}:LK:${searchValue}|OR|${NameValues.TRAILER_PLATE_NUMBER}:LK:${searchValue}`
      );
    selectedParalyzationStatus && querySearch.push(selectedParalyzationStatus);

    const querySearchString = querySearch.join('||');

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const queryParams = {
      tz: timeZone,
      search: querySearchString && `{${querySearchString}}`
    };
    const response = await DownloadEntriesInPlantUseCase.execute({
      queryParams
    });
    return response;
  }
);

export {
  getEntriesInPlantData,
  getEntriesInPlantLastUpdateData,
  getEntryInPlantDetailData,
  getWarehouseDlzDetail,
  getParalyzationSummary,
  getGlobalDlzSummaryUrl,
  downloadEntriesInPlant
};
